import {AppLanguage} from '@/logged/common/composables';
import {useUser} from '@/logged/features/user';
import {useMediaQuery} from '@malt/joy-entrypoint/src/vue/use';
import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {useOnboardingSteps} from './composables';

export const useOnboardingNewUser = defineStore('onboarding-new-user-store', () => {
    const {matchMedia} = useMediaQuery();

    const userStore = useUser();
    const onboardingSteps = useOnboardingSteps();

    const showNewUserOnboardingModal = computed(() => userStore.user?.options.newUser && !userStore.user?.options.onboarded);

    const isMobileSplashScreenDisplayed = ref(true);
    const selectedOnboardingCardIndex = ref(0);
    const selectedOnboardingCard = computed(() => onboardingSteps.value[selectedOnboardingCardIndex.value]);
    const totalOnboardingCards = computed<number>(() => onboardingSteps.value.length);
    const isAtLastStep = computed(() => selectedOnboardingCardIndex.value === totalOnboardingCards.value - 1);

    const selectedIllustation = computed<string | undefined>(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        const illustrations = selectedOnboardingCard.value.illustrations;
        if (matchMedia.value.max.Xs) {
            const foundIllustration = illustrations[selectedLanguage]?.mobile;
            return foundIllustration ?? illustrations[AppLanguage.EN]?.mobile;
        } else {
            const foundIllustration = illustrations[selectedLanguage]?.desktop;
            return foundIllustration ?? illustrations[AppLanguage.EN]?.desktop;
        }
    });

    /** Used for prefetching */
    const filteredIllustrationsByLanguage = computed<string[]>(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        return onboardingSteps.value
            .map((m) => {
                if (matchMedia.value.max.Xs) {
                    return m.illustrations[selectedLanguage]?.mobile;
                } else {
                    return m.illustrations[selectedLanguage]?.desktop;
                }
            })
            .filter((f): f is string => !!f);
    });

    async function goToNextCard() {
        if (!isAtLastStep.value) {
            selectedOnboardingCardIndex.value++;
        } else {
            await userStore.acknowledgeOnboarding();
        }
    }

    return {
        showNewUserOnboardingModal,
        selectedOnboardingCard,
        selectedOnboardingCardIndex,
        goToNextCard,
        onboardingSteps,
        selectedIllustation,
        isAtLastStep,
        isMobileSplashScreenDisplayed,
        filteredIllustrationsByLanguage,
    };
});
